import React, { useState } from 'react';
import { Calendar, User, MessageSquare, Eye, Play, Star } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { LikeButton } from './LikeButton';
import { cn } from '../../lib/utils';
import { getTagClassName } from '../../utils/tagColors';
import { getCategoryBadgeStyles } from '../../utils/categoryColors';
import { Category } from '../../services/api';
import { NeedsImprovementBadge } from './NeedsImprovementBadge';

export interface PromptCardMetrics {
  rating?: number;
  ratingCount?: number;
  uses: number;
  likes: number;
  comments: number;
}

export interface PromptCardProps {
  id: number;
  title: string;
  promptText: string;
  category: Category;
  tags: { id: number; name: string }[];
  submittedBy: string;
  createdAt: string;
  updatedAt: string;
  isOptimistic?: boolean;
  isAuthenticated?: boolean;
  isLikedByCurrentUser?: boolean;
  qualityScore?: number | null;
  metrics?: PromptCardMetrics;
  onMetricsChange?: (newMetrics: Partial<PromptCardMetrics>) => void;
  onUsePrompt?: () => void;
  onCardClick?: () => void;
}

export const PromptCard: React.FC<PromptCardProps> = ({
  id,
  title,
  promptText,
  category,
  tags,
  submittedBy,
  createdAt,
  isOptimistic,
  isAuthenticated,
  isLikedByCurrentUser,
  qualityScore,
  metrics,
  onMetricsChange,
  onUsePrompt,
  onCardClick
}) => {
  const [expanded, setExpanded] = useState(false);
  const preview = promptText.length > 200 && !expanded
    ? promptText.slice(0, 200) + '…'
    : promptText;

  return (
    <div
      className={cn(
        'group relative flex flex-col rounded-lg border border-border-subtle bg-surface shadow-xs hover:shadow-md transition-shadow focus-within:shadow-md',
        'dark:border-border-subtle/70'
      )}
      tabIndex={0}
      role="article"
      aria-label={title}
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-snug tracking-tight text-foreground/90 group-hover:text-foreground">
              {onCardClick ? (
                <button
                  type="button"
                  className="text-left hover:text-primary hover:underline focus:outline-none focus:underline focus:text-primary"
                  onClick={onCardClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCardClick();
                    }
                  }}
                >
                  {title}
                </button>
              ) : (
                title
              )}
              {isOptimistic && (
                <span className="ml-2 align-middle text-xs text-warning font-medium" aria-label="Saving">
                  Saving…
                </span>
              )}
            </h3>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge
              className="text-[11px] font-medium border transition-colors"
              style={getCategoryBadgeStyles(category.color)}
            >
              {category.name}
            </Badge>
            <NeedsImprovementBadge qualityScore={qualityScore} />
          </div>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {preview}
        </p>
        {promptText.length > 200 && (
          <Button
            type="button"
            variant="link"
            className="px-0 h-auto text-primary text-xs"
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        )}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="default"
            size="sm"
            onClick={onUsePrompt}
            className="h-8 px-3 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium"
          >
            <Play className="w-3.5 h-3.5 mr-1.5" />
            Use Prompt
          </Button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {tags.slice(0, 6).map(t => (
              <Badge
                key={t.id}
                className={getTagClassName(t.name, 'sm')}
              >
                {t.name}
              </Badge>
            ))}
            {tags.length > 6 && (
              <Badge variant="secondary" className="text-[11px]">+{tags.length - 6}</Badge>
            )}
          </div>
        )}
      </div>
      <div className="mt-auto flex items-center justify-between gap-4 border-t border-border-subtle/60 bg-surface-alt px-4 py-2.5 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <User className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{submittedBy}</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
            <time dateTime={createdAt}>{new Date(createdAt).toLocaleDateString()}</time>
          </span>
        </div>
        {metrics && (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1" aria-label={`${metrics.uses} uses`}>
              <Eye className="w-3.5 h-3.5" aria-hidden="true" />
              {metrics.uses}
            </span>
            {(metrics.ratingCount ?? 0) > 0 ? (
              <span className="inline-flex items-center gap-1" aria-label={`${metrics.rating?.toFixed(1)} stars`}>
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                {metrics.rating?.toFixed(1)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-muted-foreground/50" aria-label="No ratings">
                <Star className="w-3.5 h-3.5" aria-hidden="true" />
                <span>—</span>
              </span>
            )}
            <LikeButton
              promptId={id}
              initialLikeCount={metrics.likes}
              initialIsLiked={isLikedByCurrentUser}
              isAuthenticated={isAuthenticated}
              onLikeChange={(newLikeCount) => {
                onMetricsChange?.({ likes: newLikeCount });
              }}
            />
            <span className="inline-flex items-center gap-1" aria-label={`${metrics.comments} comments`}>
              <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />
              {metrics.comments}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
